import React from 'react';
import SafeIcon from '../../common/SafeIcon';
import { FiUsers, FiBookOpen, FiCalendar, FiBell, FiAlertTriangle, FiCheckCircle, FiClock, FiPlay, FiCpu, FiBook, FiZap } from 'react-icons/fi';

const formatIcon = { Video: FiPlay, Interactive: FiCpu, Course: FiBook, 'Micro-learning': FiZap };
const diffColor = { Beginner: 'text-green-600 bg-green-50', Intermediate: 'text-yellow-600 bg-yellow-50', Advanced: 'text-red-600 bg-red-50' };

const CampaignOverviewPanel = ({ campaign, modules, assignedLearners, assignments = [] }) => {
  const rows = assignedLearners.map(l => {
    const learnerAssignments = assignments.filter(a => String(a.employee_id) === String(l.id));
    let pct = 0;
    
    if (learnerAssignments.length > 0) {
      const completed = learnerAssignments.filter(a => a.status === 'Completed').length;
      pct = Math.round((completed / learnerAssignments.length) * 100);
    }
    
    let status = 'Not Started';
    if (pct === 100) status = 'Completed';
    else if (l.trainingStatus === 'Overdue') status = 'Overdue';
    else if (pct > 0) status = 'In Progress';

    return { pct, status };
  });

  const stats = {
    completed: rows.filter(r => r.status === 'Completed').length,
    inProgress: rows.filter(r => r.status === 'In Progress').length,
    overdue: rows.filter(r => r.status === 'Overdue').length,
    notStarted: rows.filter(r => r.status === 'Not Started').length,
  };

  return (
    <div className="space-y-4">
      {/* Status Breakdown */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
        <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
          <SafeIcon icon={FiUsers} className="text-blue-500" /> Learner Status
        </h3>
        <div className="space-y-2.5">
          {[
            { label: 'Completed', value: stats.completed, icon: FiCheckCircle, cls: 'text-emerald-600 bg-emerald-50' },
            { label: 'In Progress', value: stats.inProgress, icon: FiClock, cls: 'text-blue-600 bg-blue-50' },
            { label: 'Overdue', value: stats.overdue, icon: FiAlertTriangle, cls: 'text-red-600 bg-red-50' },
            { label: 'Not Started', value: stats.notStarted, icon: FiUsers, cls: 'text-slate-500 bg-slate-100' },
          ].map(s => (
            <div key={s.label} className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${s.cls}`}>
                  <SafeIcon icon={s.icon} className="text-xs" />
                </div>
                {s.label}
              </div>
              <span className="text-sm font-bold text-slate-800">{s.value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Modules */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
        <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
          <SafeIcon icon={FiBookOpen} className="text-violet-500" /> Training Modules
        </h3>
        <div className="space-y-2">
          {modules.map(m => {
            const Icon = formatIcon[m.deliveryFormat] || FiBook;
            return (
              <div key={m.id} className="flex items-start gap-2.5 p-2.5 rounded-lg bg-slate-50 border border-slate-100">
                <div className="w-8 h-8 bg-white border border-slate-200 rounded-lg flex items-center justify-center flex-shrink-0">
                  <SafeIcon icon={Icon} className="text-blue-500 text-sm" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-slate-700 leading-snug">{m.title}</p>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${diffColor[m.difficultyLevel]}`}>{m.difficultyLevel}</span>
                    <span className="text-xs text-slate-400">{m.duration}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Schedule */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
        <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
          <SafeIcon icon={FiCalendar} className="text-orange-500" /> Schedule
        </h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-slate-500">Start Date</span>
            <span className="font-medium text-slate-700">{campaign.startDate}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-500">Due Date</span>
            <span className="font-medium text-slate-700">{campaign.endDate}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-500">Audience</span>
            <span className="font-medium text-slate-700 text-right max-w-32 truncate">{campaign.audience}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CampaignOverviewPanel;